import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { MatIconModule } from '@angular/material/icon';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { VaFormsModule } from '@vendasta/forms';
import { MatInputModule } from '@angular/material/input';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { VaFilterModule } from '@vendasta/uikit';
import { GalaxyInfiniteScrollTriggerModule } from '@vendasta/galaxy/infinite-scroll-trigger';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { ContactType, RecipientCampaignService, RecipientType, Sender, SenderType } from '@vendasta/campaigns';
import { CrmFieldService, StandardExternalIds } from '../../../../shared-services/crm-services/field.service';
import { CrmObjectService } from '../../../../shared-services/crm-services/crm-object-service/crm-object.service';
import {
  CrmDependencies,
  CrmInjectionToken,
  PageAnalyticsInjectionToken,
  pageAnalyticsInjectionTokenGenerator,
} from '../../../../tokens-and-interfaces';
import { GalaxyDatepickerModule } from '@vendasta/galaxy/datepicker';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { RecipientV2Interface } from '@vendasta/campaigns';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { CrmObjectInterface } from '@vendasta/crm';
import { AddCampaignDialogComponent, Campaign } from './add-campaign-dialog.component';
import { format as formatDate } from 'date-fns-tz';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface dialogData {
  campaign: Campaign;
  id: string;
}
@Component({
  selector: 'crm-start-campaign-modal',
  templateUrl: './start-campaign-dialog.component.html',
  styleUrls: ['./start-campaign-dialog.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    TranslateModule,
    GalaxyLoadingSpinnerModule,
    MatDialogModule,
    GalaxyEmptyStateModule,
    MatIconModule,
    GalaxyFormFieldModule,
    MatRadioModule,
    FormsModule,
    VaFormsModule,
    MatInputModule,
    GalaxyButtonLoadingIndicatorModule,
    MatProgressSpinnerModule,
    VaFilterModule,
    GalaxyInfiniteScrollTriggerModule,
    ReactiveFormsModule,
    GalaxyTooltipModule,
    GalaxyAlertModule,
    GalaxyDatepickerModule,
    MatDatepickerModule,
  ],
  providers: [
    RecipientCampaignService,
    CrmFieldService,
    {
      provide: PageAnalyticsInjectionToken,
      useFactory: pageAnalyticsInjectionTokenGenerator('Contact'),
    },
  ],

  standalone: true,
})
export class StartCampaignDialogComponent implements OnInit {
  formGroup = new FormBuilder().nonNullable.group({
    selectFormControl: '',
    dateFormControl: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm"),
  });

  campaignStartTime: Date;
  nowDate = new Date(Date.now());
  crmObject$: Observable<CrmObjectInterface>;
  selectedCampaign: Campaign;
  loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly destroyRef: DestroyRef,
    @Inject(MAT_DIALOG_DATA) readonly modalData: dialogData,
    private dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly objectService: CrmObjectService,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    readonly ref: MatDialogRef<StartCampaignDialogComponent>,
    private snackbarService: SnackbarService,
    private readonly recipientCampaignService: RecipientCampaignService,

    private crmFieldService: CrmFieldService,
  ) {
    this.crmObject$ = objectService.getMultiObject('Contact', [this.modalData.id || '']).pipe(
      map((resp) => {
        const object = resp?.crmObjects?.[0];
        if (resp?.crmObjects?.length === 0 || !object) {
          return {};
        }
        return object;
      }),
    );
  }

  ngOnInit() {
    this.selectedCampaign = this.modalData.campaign;
  }

  sendCampaign() {
    if (this.selectedCampaign === undefined) {
      this.snackbarService.openErrorSnack('CAMPAIGNS.CONTACTS.MODAL.ERRORS.NO_CAMPAIGN_SELECTED');
      return;
    }
    let sender: Sender;
    combineLatest([this.config.namespace$, this.config.parentNamespace$])
      .pipe(take(1))
      .subscribe(([namespace, parentNamespace]) => {
        let senderType = SenderType.SENDER_TYPE_PARTNER;
        if (parentNamespace) {
          senderType = SenderType.SENDER_TYPE_BUSINESS;
        }
        sender = {
          type: senderType,
          id: namespace,
        } as Sender;
      });

    const recipient: Observable<RecipientV2Interface> = this.crmObject$.pipe(
      map((object) => {
        return {
          id: object.crmObjectId,
          type: RecipientType.RECIPIENT_TYPE_CRM_CONTACT,
          contact: {
            type: ContactType.CONTACT_TYPE_EMAIL,
            value: this.crmFieldService.getFieldValueFromCrmObject(object, StandardExternalIds.Email)?.stringValue,
          },
          emailAddress: this.crmFieldService.getFieldValueFromCrmObject(object, StandardExternalIds.Email)?.stringValue,
          phoneNumber: this.crmFieldService.getFieldValueFromCrmObject(object, StandardExternalIds.PhoneNumber)
            ?.stringValue,
        };
      }),
    );

    this.loading$$.next(true);
    recipient
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((rec) => {
          const formValues = this.formGroup.getRawValue();

          this.campaignStartTime = formValues.dateFormControl
            ? new Date(formValues.dateFormControl)
            : new Date(Date.now());

          return this.recipientCampaignService
            .addToCampaign(this.selectedCampaign.id, sender, rec, this.campaignStartTime)
            .pipe(
              map(() => ''),
              catchError(() => {
                return of(rec.contact?.value || rec.emailAddress);
              }),
            );
        }),
      )
      .subscribe((resp) => {
        const failedAddresses = resp !== '' ? [resp] : [];
        if (failedAddresses.length > 0) {
          this.snackbarService.openErrorSnack(this.translate.instant('ASSOCIATIONS.CAMPAIGNS.MODAL.ERROR'));
        } else {
          this.loading$$.next(false);
          this.snackbarService.openSuccessSnack(
            this.translate.instant('ASSOCIATIONS.CAMPAIGNS.MODAL.SUCCESS') + ' ' + this.selectedCampaign.title,
          );
        }
        this.close();
      });
  }

  close() {
    this.ref.close();
  }

  back() {
    this.ref.close();
    this.dialog.open(AddCampaignDialogComponent, {
      width: '500px',
      data: {
        id: this.modalData.id,
      },
    });
  }
}
